import React, { useEffect, useLayoutEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, X, Sparkles, Check } from "lucide-react";

const STEPS = [
  { title: "Bienvenue sur FlowDesk 👋", body: "FlowDesk réunit vos emails, tickets, tâches et clients au même endroit. Suivons ensemble une visite d'une minute pour tout configurer.", route: "/", center: true },
  { selector: '[data-testid="company-switcher"]', title: "1. Vos entreprises", body: "Gérez plusieurs entreprises et basculez de l'une à l'autre ici. Chaque espace est totalement isolé (emails, tickets, clients…).", route: "/", place: "right" },
  { selector: '[data-testid="nav-companies"]', title: "2. Configurer une entreprise", body: "Créez et paramétrez vos entreprises : logo, couleurs, portail client de marque, SMTP et licence.", route: "/", place: "right" },
  { selector: '[data-testid="nav-mail"]', title: "3. Connecter une boîte email", body: "Reliez vos boîtes IMAP/SMTP (Gmail, Outlook, OVH…) via « Comptes email », puis transformez un email en ticket ou en tâche en un clic.", route: "/", place: "right" },
  { selector: '[data-testid="nav-tickets"]', title: "4. Gérer les tickets", body: "Centralisez les demandes clients : assignez, répondez, suivez les statuts (Ouvert, Répondu, Fermé) et convertissez en tâche.", route: "/", place: "right" },
  { selector: '[data-testid="nav-tasks"]', title: "5. Suivre les tâches", body: "Pilotez l'avancement, le temps passé et la rentabilité. Faites valider les livrables directement par le client.", route: "/", place: "right" },
  { selector: '[data-testid="nav-chat"]', title: "6. Chatter avec vos clients", body: "Une messagerie temps réel façon WhatsApp pour échanger rapidement avec vos clients.", route: "/", place: "right" },
  { selector: '[data-testid="nav-clients"]', title: "7. Vue 360° clients", body: "Retrouvez tout l'historique de chaque client et de vos contacts au même endroit.", route: "/", place: "right" },
  { selector: '[data-testid="nav-members"]', title: "8. Inviter votre équipe", body: "Ajoutez vos collègues et définissez leurs rôles et permissions.", route: "/", place: "right" },
  { selector: '[data-testid="tour-help-btn"]', title: "C'est parti ! 🚀", body: "Vous pourrez relancer cette visite à tout moment via ce bouton d'aide. Bonne prise en main de FlowDesk !", route: "/", place: "bottom" },
];

const PAD = 8;
const TW = 330;

export function GuidedTour({ onClose }) {
  const navigate = useNavigate();
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const step = STEPS[idx];
  const isLast = idx === STEPS.length - 1;

  const measure = useCallback(() => {
    if (!step.selector) { setRect(null); return; }
    const el = document.querySelector(step.selector);
    if (el) {
      el.scrollIntoView({ block: "nearest", inline: "nearest" });
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    } else {
      setRect(null);
    }
  }, [step]);

  useEffect(() => {
    if (step.route) navigate(step.route);
    let tries = 0;
    const id = setInterval(() => { measure(); if (++tries >= 6) clearInterval(id); }, 90);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [idx]);

  useLayoutEffect(() => {
    const on = () => measure();
    window.addEventListener("resize", on);
    window.addEventListener("scroll", on, true);
    return () => { window.removeEventListener("resize", on); window.removeEventListener("scroll", on, true); };
  }, [measure]);

  const finish = () => { localStorage.setItem("fd_tour_done", "1"); onClose?.(); };
  const next = () => (isLast ? finish() : setIdx((i) => i + 1));
  const prev = () => setIdx((i) => Math.max(0, i - 1));

  // tooltip position
  let tip = { top: 0, left: 0, centered: false };
  if (!rect || step.center) {
    tip.centered = true;
  } else {
    const place = step.place || "right";
    if (place === "bottom") {
      tip.top = Math.min(window.innerHeight - 220, rect.top + rect.height + 14);
      tip.left = Math.min(window.innerWidth - TW - 12, Math.max(12, rect.left + rect.width / 2 - TW / 2));
    } else {
      tip.left = rect.left + rect.width + 16;
      tip.top = Math.max(12, Math.min(window.innerHeight - 240, rect.top - 6));
      if (tip.left + TW > window.innerWidth) { // fallback below
        tip.left = Math.min(window.innerWidth - TW - 12, Math.max(12, rect.left));
        tip.top = rect.top + rect.height + 14;
      }
    }
  }

  const node = (
    <div data-testid="guided-tour" className="fixed inset-0 z-[9997]">
      {/* click blocker */}
      <div className="absolute inset-0" onClick={(e) => e.stopPropagation()} />
      {/* spotlight */}
      {rect && !step.center ? (
        <div className="absolute rounded-xl transition-all duration-300" style={{
          top: rect.top - PAD, left: rect.left - PAD, width: rect.width + PAD * 2, height: rect.height + PAD * 2,
          boxShadow: "0 0 0 9999px rgba(6,10,20,0.72)", border: "2px solid #4F9CFF", pointerEvents: "none",
        }} />
      ) : (
        <div className="absolute inset-0" style={{ background: "rgba(6,10,20,0.72)", pointerEvents: "none" }} />
      )}

      {/* tooltip */}
      <div data-testid="tour-tooltip"
        className="absolute w-[330px] max-w-[calc(100vw-24px)] bg-card text-foreground border border-klein/30 rounded-2xl shadow-2xl p-4 animate-in fade-in zoom-in-95 duration-200"
        style={tip.centered ? { top: "50%", left: "50%", transform: "translate(-50%,-50%)" } : { top: tip.top, left: tip.left }}>
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-klein/15 text-klein flex items-center justify-center shrink-0"><Sparkles size={15} /></div>
            <h3 className="font-cabinet font-bold text-[15px] leading-tight">{step.title}</h3>
          </div>
          <button data-testid="tour-skip" onClick={finish} title="Fermer la visite" className="p-1 rounded-md text-zinc-400 hover:bg-white/10 shrink-0"><X size={16} /></button>
        </div>
        <p className="text-[13px] leading-relaxed text-foreground/80 mb-3">{step.body}</p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1" data-testid="tour-step-indicator">
            {STEPS.map((_, i) => <span key={i} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-4 bg-klein" : "w-1.5 bg-white/20"}`} />)}
          </div>
          <div className="flex items-center gap-1.5">
            {idx > 0 && <button data-testid="tour-prev" onClick={prev} className="flex items-center gap-1 h-8 px-2.5 rounded-lg text-xs font-medium text-foreground/70 hover:bg-foreground/[0.06]"><ArrowLeft size={13} /> Précédent</button>}
            <button data-testid="tour-next" onClick={next} className="flex items-center gap-1.5 h-8 px-3.5 rounded-lg bg-klein text-white text-xs font-semibold hover:brightness-110">
              {isLast ? <>Terminer <Check size={14} /></> : <>Suivant <ArrowRight size={14} /></>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
  return createPortal(node, document.body);
}
